import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../../api/axios";
import DOMPurify from "dompurify";

export default function EmploymentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("ko") ? "ko" : "en";

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const fmtDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d);
    try {
      return new Intl.DateTimeFormat(locale === "ko" ? "ko-KR" : "en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      }).format(dt);
    } catch {
      return dt.toISOString().slice(0, 10);
    }
  };

  const load = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      // 사용자 상세: GET /api/employments/detail/{id}
      const res = await api.get(`/api/employments/detail/${id}`, {
        headers: { "Accept-Language": locale },
      });

      const resultCode = res.data?.resultCode;
      if (resultCode !== "200" && resultCode !== 200) {
        setErrorMsg(res.data?.resultMessage || t("employment.errors.notFound"));
        setItem(null);
        return;
      }

      setItem(res.data?.data ?? null);
    } catch (e) {
      console.error(e);
      setErrorMsg(
        e.response?.data?.resultMessage ||
          t(
            "employment.errors.fetchDetail",
            "상세를 불러오는 중 오류가 발생했습니다.",
          ),
      );
      setItem(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, locale]);

  const safeHtml = item?.content ? DOMPurify.sanitize(item.content) : "";

  return (
    <div className="w-full">
      <div className="mx-auto max-w-4xl px-4 py-8">
        {/* 헤더 */}
        <div className="mb-5 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
              {t("employment.detail.title", "채용공고 상세")}
            </h1>
            <p className="mt-1 text-xs text-slate-500">ID: {id}</p>
          </div>

          <button
            type="button"
            onClick={() => nav(-1)}
            className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            {t("common.back", "뒤로가기")}
          </button>
        </div>

        {/* 상태 */}
        {loading && (
          <div className="rounded-2xl border bg-white p-8 text-center text-slate-500 shadow-sm">
            {t("employment.loading", "불러오는 중...")}
          </div>
        )}

        {!loading && errorMsg && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
            {errorMsg}
          </div>
        )}

        {!loading && !errorMsg && item && (
          <div className="rounded-2xl border bg-white p-6 shadow-sm md:p-8">
            {/* 상단 메타 */}
            <div className="mb-2 text-xs text-slate-500">
              {t("employment.meta.created", "작성일")}:{" "}
              {fmtDate(item.createdAt)}
              {"  ·  "}
              {t("employment.meta.deadline", "마감일")}:{" "}
              {fmtDate(item.deadline)}
            </div>

            {/* 제목 */}
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">
              {item.title}
            </h2>

            {/* 회사/근무지 */}
            <div className="mt-3 text-sm text-slate-700">
              <span className="font-semibold">{item.company}</span>
              {item.locationText ? ` · ${item.locationText}` : ""}
            </div>

            {/* 요약 */}
            {item.summary && (
              <div className="mt-5 rounded-xl border bg-slate-50 p-4 text-sm text-slate-700">
                <div className="mb-1 text-xs font-semibold text-slate-500">
                  {t("employment.detail.summary", "요약")}
                </div>
                <div className="whitespace-pre-line">{item.summary}</div>
              </div>
            )}

            {/* 본문(HTML) */}
            <div
              className="prose mt-6 max-w-none text-sm leading-relaxed text-slate-800 prose-a:text-blue-600 prose-a:underline prose-img:rounded-xl prose-img:shadow-sm md:text-base"
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />

            {/* 상세 링크(외부) - 버튼으로만 제공 */}
            {item.detailUrl && (
              <div className="mt-8 flex justify-end border-t pt-4">
                <a
                  href={item.detailUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {t("employment.detail.openLink", "공고 링크 열기")}
                </a>
              </div>
            )}

            {/* 하단 액션 영역 */}
            <div className="mt-8 flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={() => nav(-1)}
                className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {t("common.back", "뒤로가기")}
              </button>

              <button
                type="button"
                onClick={() => nav(`/employment/${item.id}/inquiry`)}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                {t("employment.detail.apply", "지원하기")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
